import { ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { ChipComponent } from '../chip/chip.component';
import { AccordionConfig, AccordionWithIsOpen } from '../../data-access/models/accordion-chip-data.model';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'common-ui-components-accordion',
  standalone: true,
  imports: [CommonModule, DpIconComponent, ChipComponent],
  templateUrl: './accordion.component.html',
  styleUrl: './accordion.component.scss',
  animations: [
    trigger('showDetails', [
      state(
        'void',
        style({
          height: 0,
        }),
      ),
      state(
        '*',
        style({
          height: '*',
        }),
      ),
      transition('void <=> *', [animate('0.2s')]),
    ]),
    trigger('rotateChevron', [
      state(
        'close',
        style({
          rotate: '0deg',
        }),
      ),
      state(
        'open',
        style({
          rotate: '180deg',
        }),
      ),
      transition('open <=> close', [animate('0.2s')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccordionComponent<T extends AccordionWithIsOpen> {
  // Inputs
  configs = input.required<AccordionConfig<T>[]>();
  multipleOpen = input<boolean>(false);
  wrapperClasses = input<string>();

  // Models
  showDetails = signal<boolean[]>([]);

  // Variables
  inputsToPass = signal<Record<string, unknown>[]>([]);

  constructor() {
    effect(
      () => {
        if (this.configs().length) {
          this.initData();
        }
      },
      { allowSignalWrites: true },
    );
  }

  initData(): void {
    this.showDetails.set([]);
    const booleans: boolean[] = [];
    const inputs: Record<string, unknown>[] = [];
    this.configs().forEach((config) => {
      booleans.push(config.isOpen);
      inputs.push({ ...config.inputs, isOpen: config.isOpen });
    });
    this.showDetails.set(booleans);
    this.inputsToPass.set(inputs);
  }

  toggleDetails(index: number): void {
    if (this.multipleOpen()) {
      const modifiedBooleans = this.showDetails();
      const modifiedInputs = this.inputsToPass();
      modifiedBooleans[index] = !modifiedBooleans[index];
      modifiedInputs[index] = { ...modifiedInputs[index], isOpen: !modifiedInputs[index]['isOpen'] };
      this.showDetails.set(modifiedBooleans);
    } else {
      const booleans: boolean[] = [];
      const inputs: Record<string, unknown>[] = [];
      this.showDetails().forEach((config, i) => {
        const boolean = i === index ? !this.showDetails()[index] : false;
        booleans.push(boolean);
        inputs.push({ ...this.inputsToPass()[i], isOpen: boolean });
        return boolean;
      });
      this.showDetails.set(booleans);
      this.inputsToPass.set(inputs);
    }
  }
}
